import { IElementWaypoint } from "@/hooks/use-placeholders";
import { useAppSelector } from "@/store/store-hooks";
import { selectIElementFloorPosition } from "@/utils/camera-transform";
import {
  roundedCalculatePosition,
  TransformOverrides,
} from "@faro-lotv/app-component-toolbox";
import { FaroTooltip } from "@faro-lotv/flat-ui";
import Box from "@mui/material/Box";
import { isEqual } from "es-toolkit";
import { PropsWithChildren, type JSX } from "react";
import { AppAwareHtml } from "../renderers/app-aware-html";
import { useWaypointReference } from "./placeholder-preview";

type PlaceholderHoverTooltipProps = {
  /** The placeholder currently being hovered */
  placeholder?: IElementWaypoint;

  /** Optional transform overrides to apply for the position calculation. */
  transformOverrides?: TransformOverrides;
};

/** Equivalent size of a placeholder in ui space. Used to position the tooltip. */
const PLACEHOLDER_SPACER_SIZE = 12;

/**
 * @returns A tooltip that is displayed over a hovered waypoint placeholder.
 */
export function PlaceholderHoverTooltip({
  placeholder,
  transformOverrides,
  children,
}: PropsWithChildren<PlaceholderHoverTooltipProps>): JSX.Element | null {
  const referenceElement = useWaypointReference(placeholder);

  const position = useAppSelector(
    (state) =>
      // use the floor position to avoid the placeholder being placed out of view in (video mode) paths with large vertical components
      placeholder
        ? selectIElementFloorPosition(placeholder, transformOverrides)(state)
        : undefined,
    isEqual,
  );

  if (!position || !referenceElement) return null;

  return (
    <AppAwareHtml
      position={position}
      calculatePosition={roundedCalculatePosition}
      style={{ pointerEvents: "none" }}
    >
      <FaroTooltip title={children} placement="bottom" open>
        {/* Box is used to give the tooltip a stand-in ui element for the placeholder rendered in 3d */}
        <Box
          component="div"
          sx={{
            width: PLACEHOLDER_SPACER_SIZE,
            height: PLACEHOLDER_SPACER_SIZE,
          }}
        />
      </FaroTooltip>
    </AppAwareHtml>
  );
}
